/**
 * API client for Let's Be Real backend
 */

// Determine API base URL based on current hostname
// In production (Firebase), use relative URLs (same domain)
// In development, use localhost backend
const getApiBaseUrl = () => {
  // Check if we're on the production domain
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'letsbereal.com.au' || hostname === 'www.letsbereal.com.au' || hostname === 'lets-be-real-fc550.web.app' || hostname === 'lets-be-real-fc550.firebaseapp.com') {
      return ''; // Use relative URLs for production
    }
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3000'; // Development
    }
  }
  // Fallback: if PROD env is set, use relative URLs
  return import.meta.env.PROD ? '' : 'http://localhost:3000';
};

const API_BASE_URL = getApiBaseUrl();

export interface CategoryData {
  summary: string;
  impact: string;
}

export interface PropertyReport {
  address: string;
  tldr?: string;
  estimated_value_range?: string;
  neighbourhood_vibe?: string;
  market_momentum?: string;
  community_sentiment?: string;
  lifestyle_fit?: string;
  nearby_highlights?: string[];
  confidence_score?: number;
  accessibility?: CategoryData;
  noise_traffic?: CategoryData;
  local_development?: CategoryData;
  environment?: CategoryData;
  demographics?: CategoryData;
  market_dynamics?: CategoryData;
  gentrification_index?: CategoryData;
  investor_thought?: string;
  owner_occupier_thought?: string;
}

export interface AddressSuggestion {
  display_name: string;
  address_parts: {
    suburb?: string;
    city?: string;
    state?: string;
    postcode?: string;
  };
}

export interface TimeEstimate {
  estimated_seconds: number;
  breakdown: {
    geocoding: number;
    poi_fetch: number;
    ai_generation: number;
    total: number;
  };
}

export interface QuickFactsResponse {
  address: string;
  raw_data: any; // Raw data from backend (POI, ABS, etc.)
  scores: any;   // Scoring data
}

export class APIError extends Error {
  constructor(message: string, public statusCode?: number) {
    super(message);
    this.name = 'APIError';
  }
}

/**
 * Fetch quick facts (data without AI) - Progressive loading phase 1
 * Returns in 5-8 seconds with raw data and scores
 */
export async function fetchQuickFacts(address: string): Promise<QuickFactsResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/report/quick-facts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ address }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new APIError(errorMessage, response.status);
    }

    const data = await response.json();
    return data as QuickFactsResponse;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    
    if (error instanceof TypeError) {
      throw new APIError('Unable to connect to server. Please check your internet connection.');
    }
    
    throw new APIError(error instanceof Error ? error.message : 'An unknown error occurred');
  }
}

/**
 * Fetch full property report with AI enhancement
 */
export async function fetchPropertyReport(address: string): Promise<PropertyReport> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/report`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ address }),
    });

    if (!response.ok) {
      // Try to parse error message from response
      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new APIError(errorMessage, response.status);
    }

    const data = await response.json();
    return data as PropertyReport;
  } catch (error) {
    // Handle network errors or parse errors
    if (error instanceof APIError) {
      throw error;
    }
    
    if (error instanceof TypeError) {
      throw new APIError('Unable to connect to server. Please check your internet connection.');
    }
    
    throw new APIError(error instanceof Error ? error.message : 'An unknown error occurred');
  }
}

/**
 * Fetch address suggestions for autocomplete
 */
export async function fetchAddressSuggestions(query: string): Promise<AddressSuggestion[]> {
  // Don't call API for short queries
  if (!query || query.trim().length < 3) {
    return [];
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/autocomplete?q=${encodeURIComponent(query)}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      // Silently fail for autocomplete errors
      console.warn('Autocomplete failed:', response.status);
      return [];
    }

    const data = await response.json();
    return data.suggestions || [];
  } catch (error) {
    // Silently fail for autocomplete errors - not critical
    console.warn('Autocomplete error:', error);
    return [];
  }
}

export async function estimateReportTime(address: string): Promise<TimeEstimate> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/estimate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ address }),
    });

    if (!response.ok) {
      // Return default estimate if API fails
      return {
        estimated_seconds: 20,
        breakdown: {
          geocoding: 2,
          poi_fetch: 12,
          ai_generation: 8,
          total: 20,
        },
      };
    }

    return await response.json();
  } catch (error) {
    // Return default estimate if fetch fails
    console.warn('Estimate error:', error);
    return {
      estimated_seconds: 20,
      breakdown: {
        geocoding: 2,
        poi_fetch: 12,
        ai_generation: 8,
        total: 20,
      },
    };
  }
}

/**
 * Health check for backend
 */
export async function checkBackendHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'GET',
    });
    return response.ok;
  } catch {
    return false;
  }
}

